import * as THREE from 'three';

/**
 * Interactive 3D Monolith Double-Arch Portal Scene
 */
export function initThreeMonolith(containerId = 'monolithThreeContainer') {
  const container = document.getElementById(containerId);
  if (!container) return;

  const width = container.clientWidth || 360;
  const height = container.clientHeight || 220;

  // 1. Scene & Camera
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(42, width / height, 0.1, 100);
  camera.position.set(0, 0, 4.8);

  // 2. WebGL Renderer with Alpha
  const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
  renderer.setSize(width, height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.2;
  renderer.domElement.className = 'hero-threejs-canvas';
  container.appendChild(renderer.domElement);

  // 3. Lighting
  const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
  scene.add(ambientLight);

  const goldLight = new THREE.PointLight(0xf5cf53, 4.0, 15);
  goldLight.position.set(2, 3, 3);
  scene.add(goldLight);

  const frontFill = new THREE.DirectionalLight(0xfff0bb, 1.4);
  frontFill.position.set(0, 2, 5);
  scene.add(frontFill);

  const cyanRimLight = new THREE.PointLight(0x38bdf8, 2.2, 10);
  cyanRimLight.position.set(-3, -2, -2);
  scene.add(cyanRimLight);

  // 4. Construct 3D Monolith Double-Arch Group
  const monolithGroup = new THREE.Group();

  const goldMaterial = new THREE.MeshStandardMaterial({
    color: 0xdfb743,
    metalness: 0.88,
    roughness: 0.18,
    emissive: 0x221600,
    emissiveIntensity: 0.4
  });

  const goldInnerMaterial = new THREE.MeshStandardMaterial({
    color: 0xfae188,
    metalness: 0.92,
    roughness: 0.14,
    emissive: 0x332000,
    emissiveIntensity: 0.5
  });

  // Helper to create 3D Arch Mesh
  function createArch(outerWidth, outerHeight, thickness, depth, material) {
    const group = new THREE.Group();

    // Left pillar
    const pillarGeo = new THREE.BoxGeometry(thickness, outerHeight - thickness, depth);
    const leftPillar = new THREE.Mesh(pillarGeo, material);
    leftPillar.position.set(-outerWidth / 2 + thickness / 2, -(thickness / 2), 0);
    group.add(leftPillar);

    // Right pillar
    const rightPillar = new THREE.Mesh(pillarGeo, material);
    rightPillar.position.set(outerWidth / 2 - thickness / 2, -(thickness / 2), 0);
    group.add(rightPillar);

    // Top beam
    const beamGeo = new THREE.BoxGeometry(outerWidth, thickness, depth);
    const topBeam = new THREE.Mesh(beamGeo, material);
    topBeam.position.set(0, outerHeight / 2 - thickness / 2, 0);
    group.add(topBeam);

    return group;
  }

  // Outer Arch (matching monolith logo proportions)
  const outerArch = createArch(1.8, 2.2, 0.16, 0.18, goldMaterial);
  monolithGroup.add(outerArch);

  // Inner Arch (nested inside)
  const innerArch = createArch(1.2, 1.6, 0.14, 0.16, goldInnerMaterial);
  innerArch.position.set(0, -0.15, 0);
  monolithGroup.add(innerArch);

  scene.add(monolithGroup);

  // 5. Orbiting Ambient Dust Particles
  const particleCount = 45;
  const particleGeo = new THREE.BufferGeometry();
  const particlePositions = new Float32Array(particleCount * 3);

  for (let i = 0; i < particleCount * 3; i += 3) {
    particlePositions[i] = (Math.random() - 0.5) * 5;
    particlePositions[i + 1] = (Math.random() - 0.5) * 4;
    particlePositions[i + 2] = (Math.random() - 0.5) * 3;
  }
  particleGeo.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));

  const particleMat = new THREE.PointsMaterial({
    color: 0xf5cf53,
    size: 0.04,
    transparent: true,
    opacity: 0.65
  });
  const particles = new THREE.Points(particleGeo, particleMat);
  scene.add(particles);

  // 6. Interactive Motion & Mouse Parallax
  let mouseX = 0;
  let mouseY = 0;
  let targetRotationX = 0;
  let targetRotationY = 0;
  let spinVelocity = 0;

  window.addEventListener('mousemove', (e) => {
    const normX = (e.clientX / window.innerWidth) * 2 - 1;
    const normY = -(e.clientY / window.innerHeight) * 2 + 1;
    targetRotationY = normX * 0.45;
    targetRotationX = -normY * 0.35;
  });

  // Click on 3D Monolith: trigger dynamic spin boost & light flare
  container.addEventListener('click', () => {
    spinVelocity += 0.35;
    goldLight.intensity = 7.0;
    setTimeout(() => {
      goldLight.intensity = 3.5;
    }, 450);
  });

  // Resize handler
  function onResize() {
    if (!container) return;
    const newW = container.clientWidth || 360;
    const newH = container.clientHeight || 220;
    camera.aspect = newW / newH;
    camera.updateProjectionMatrix();
    renderer.setSize(newW, newH);
  }
  window.addEventListener('resize', onResize);

  // 7. Animation Loop
  const startMs = performance.now();

  function animate() {
    requestAnimationFrame(animate);
    const elapsedTime = (performance.now() - startMs) * 0.001;

    // Floating bob
    monolithGroup.position.y = Math.sin(elapsedTime * 1.5) * 0.08;

    // Smooth inertia tilt toward mouse
    monolithGroup.rotation.x += (targetRotationX - monolithGroup.rotation.x) * 0.06;
    monolithGroup.rotation.y += (targetRotationY + (elapsedTime * 0.4) + spinVelocity - monolithGroup.rotation.y) * 0.06;

    // Decay spin velocity back to normal
    spinVelocity *= 0.94;

    // Orbit particles
    particles.rotation.y = elapsedTime * 0.08;

    renderer.render(scene, camera);
  }

  animate();
}
